import { useModal } from "@hooks";
import {
  MeetingRoomActivities,
  MeetingRoomAvailabilityInformationContent,
  MeetingRoomDetailsCardBottom,
  MeetingRoomEquipment,
  MeetingRoomImage,
  MeetingRoomInfo,
  MeetingRoomMap,
} from "@organisms";
import {
  useAppSelector,
  MeetingRoom,
  getFloorHasMap,
  getIsMeetingsCalendarActive,
  getMeetingRoomDynamicFreeTimeslot,
  getSuggestionDataForRoomIdForRequest,
} from "@lib/store";
import { DEFAULT_NEW_EVENT_DURATION as DURATION } from "@constants";
import { MeetingRoomData } from "src/components/organisms/meetingRoom/MeetingRoomData";
import { useTranslation } from "react-i18next";
import { useBreakPoint } from "@hooks";
import { FlexCol } from "@quarks";
import {
  useMeetingRoomRequestContext,
  useRoomScheduleStatusForRequest,
  useRoomSensorStatusForRequest,
} from "@lib/logic";
import { StyledModalPage } from "./styles";
import { AvailableRoomBookingDetailsModalPage } from "./AvailableRoomBookingDetailsModalPage";
import { LocationBasedServices } from "../../LocationBasedServices";

type Props = {
  room: MeetingRoom;
  startDateTime: string;
  endDateTime: string;
};

export const AvailableRoomInformationModal = ({ room, startDateTime, endDateTime }: Props) => {
  const { closeModal, addPages, removePages } = useModal();

  const { t } = useTranslation();
  const request = useMeetingRoomRequestContext({
    buildingId: room.buildingId,
    selectedFloorId: room.floorId ?? null,
    startDateTime,
    endDateTime,
  });

  const scheduleStatus = useRoomScheduleStatusForRequest(request, room.id, room.email);
  const sensorStatus = useRoomSensorStatusForRequest(request, room.id);
  const meetingRoomSuggestion = useAppSelector(state => getSuggestionDataForRoomIdForRequest(state, request, room.id));
  const dynamicTimeslot = useAppSelector(state =>
    getMeetingRoomDynamicFreeTimeslot(state, request, room.id, startDateTime, {
      minutes: DURATION,
    }),
  );
  const floorHasMap = useAppSelector(getFloorHasMap(room.floorId));
  const breakPoint = useBreakPoint();
  const showMap = (floorHasMap && breakPoint === "small") || floorHasMap;
  const isMeetingsCalendarActive = useAppSelector(getIsMeetingsCalendarActive);

  return (
    <>
      <StyledModalPage
        className="p-0"
        onClose={closeModal}
        title={t("screen:MeetingRoomDetails")}
        bottom={
          !!room.email && isMeetingsCalendarActive ? (
            <FlexCol className={"px-[var(--padding-x)] py-[var(--padding-y)] border-t"}>
              <MeetingRoomDetailsCardBottom
                dynamicTimeslot={dynamicTimeslot}
                meetingRoomSuggestion={meetingRoomSuggestion}
                onBookingDetailsPress={() =>
                  addPages([
                    <AvailableRoomBookingDetailsModalPage
                      room={room}
                      dynamicTimeslot={dynamicTimeslot}
                      onClose={closeModal}
                      onBack={() => removePages(1)}
                    />,
                  ])
                }
                room={room}
                onBookNowCallback={closeModal}
              />
            </FlexCol>
          ) : undefined
        }>
        <FlexCol
          as="section"
          gap={32}
          className={"px-[var(--padding-x)] py-[var(--padding-y)]"}>
          <MeetingRoomInfo
            roomName={room.displayName}
            buildingName={room.buildingName}
            floorName={room.floorName}
            capacity={room.capacity}
          />
          <FlexCol>
            <MeetingRoomImage
              displayName={room.displayName}
              email={room.email}
              roomPictureHash={room.roomPictureHash}
              buildingId={room.buildingId}
            />
          </FlexCol>
          <MeetingRoomData
            sensorStatus={sensorStatus}
            scheduleStatus={scheduleStatus}
            onLearnMorePress={() =>
              addPages([
                <StyledModalPage
                  onClose={closeModal}
                  title={t("screen:MeetingRoomDetails")}
                  className="pb-0"
                  onBack={() => removePages(1)}>
                  <MeetingRoomAvailabilityInformationContent
                    className={"px-[var(--padding-x)] py-[var(--padding-y)]"}
                  />
                </StyledModalPage>,
              ])
            }
          />
          <MeetingRoomActivities activities={room.activities} />
          <MeetingRoomEquipment equipment={room.equipment} />
          {showMap && <MeetingRoomMap room={room} />}
          <LocationBasedServices
            buildingId={room.buildingId}
            locationType="MeetingRoom"
            locationName={room.displayName}
          />
        </FlexCol>
      </StyledModalPage>
    </>
  );
};
